/**
 * ClarificationQA Component
 * Interactive interface for clients to answer AI-generated clarification questions
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  LinearProgress,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Paper,
} from '@mui/material';
import {
  QuestionAnswer as QuestionIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { EstimationService } from '../../services/EstimationService';
import {
  EstimationClarification,
  ClarificationQuestionType,
  CLARIFICATION_TYPE_LABELS,
} from '../../types/estimation';

interface ClarificationQAProps {
  estimationId: number;
  clarifications: EstimationClarification[];
  onAnswersSubmitted?: () => void;
  readonly?: boolean;
}

export const ClarificationQA: React.FC<ClarificationQAProps> = ({
  estimationId,
  clarifications,
  onAnswersSubmitted,
  readonly = false,
}) => {
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Initialize answers from existing clarifications
  useEffect(() => {
    const initialAnswers: Record<number, string> = {};
    clarifications.forEach((clarification) => {
      if (clarification.answer) {
        initialAnswers[clarification.id] = clarification.answer;
      }
    });
    setAnswers(initialAnswers);
  }, [clarifications]);

  const handleAnswerChange = (clarificationId: number, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [clarificationId]: value,
    }));
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(false);

    // Validate required questions are answered
    const unansweredRequired = clarifications.filter(
      (c) => c.is_required && !answers[c.id]?.trim()
    );

    if (unansweredRequired.length > 0) {
      setError(
        `Please answer all required questions. ${unansweredRequired.length} required ${
          unansweredRequired.length === 1 ? 'question' : 'questions'
        } remaining.`
      );
      return;
    }

    setLoading(true);

    try {
      // Only send answered questions
      const answersToSubmit = Object.entries(answers)
        .filter(([_, answer]) => answer.trim())
        .map(([clarificationId, answer]) => ({
          clarification_id: parseInt(clarificationId),
          answer: answer.trim(),
        }));

      await EstimationService.answerClarifications(estimationId, {
        answers: answersToSubmit,
      });

      setSuccess(true);
      setError(null);

      // Call callback after short delay
      setTimeout(() => {
        if (onAnswersSubmitted) {
          onAnswersSubmitted();
        }
      }, 1500);
    } catch (err: any) {
      console.error('Error submitting answers:', err);
      setError(
        err.response?.data?.message ||
          'Failed to submit answers. Please try again.'
      );
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  // Calculate progress
  const requiredQuestions = clarifications.filter((c) => c.is_required);
  const answeredRequired = requiredQuestions.filter(
    (c) => c.is_answered || (answers[c.id] && answers[c.id].trim())
  );
  const progress =
    requiredQuestions.length > 0
      ? Math.round((answeredRequired.length / requiredQuestions.length) * 100)
      : 100;

  // Group questions by type
  const questionsByType = clarifications.reduce((acc, clarification) => {
    const type = clarification.question_type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(clarification);
    return acc;
  }, {} as Record<ClarificationQuestionType, EstimationClarification[]>);

  const getQuestionTypeColor = (type: ClarificationQuestionType): string => {
    const colors: Record<ClarificationQuestionType, string> = {
      technical: 'primary',
      business: 'secondary',
      scope: 'info',
      timeline: 'warning',
      budget: 'success',
      resources: 'error',
      integration: 'primary',
      compliance: 'warning',
    };
    return colors[type] || 'default';
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            <QuestionIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Clarification Questions
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Please answer the following questions to help us better understand
            your project requirements. Questions marked with an asterisk (*) are
            required.
          </Typography>

          {/* Progress Bar */}
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Progress: {answeredRequired.length} of {requiredQuestions.length}{' '}
                required questions answered
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {progress}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Answers submitted successfully! Specification generation will begin
            shortly.
          </Alert>
        )}

        <Stack spacing={3}>
          {Object.entries(questionsByType).map(([type, questions]) => (
            <Box key={type}>
              <Chip
                label={
                  CLARIFICATION_TYPE_LABELS[type as ClarificationQuestionType]
                }
                color={
                  getQuestionTypeColor(type as ClarificationQuestionType) as any
                }
                size="small"
                sx={{ mb: 2 }}
              />
              <Stack spacing={2}>
                {questions
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map((clarification) => {
                    const isAnswered =
                      clarification.is_answered ||
                      (answers[clarification.id] &&
                        answers[clarification.id].trim());

                    return (
                      <Paper
                        key={clarification.id}
                        elevation={1}
                        sx={{
                          p: 2,
                          border: clarification.is_required
                            ? '1px solid'
                            : undefined,
                          borderColor: clarification.is_required
                            ? 'primary.main'
                            : undefined,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            mb: 1.5,
                          }}
                        >
                          {isAnswered ? (
                            <CheckIcon
                              color="success"
                              sx={{ mt: 0.5, fontSize: 20 }}
                            />
                          ) : (
                            <UncheckedIcon
                              color="disabled"
                              sx={{ mt: 0.5, fontSize: 20 }}
                            />
                          )}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight="medium">
                              {clarification.question}
                              {clarification.is_required && (
                                <Typography
                                  component="span"
                                  color="error"
                                  sx={{ ml: 0.5 }}
                                >
                                  *
                                </Typography>
                              )}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Round {clarification.clarification_round} •{' '}
                              {clarification.is_required
                                ? 'Required'
                                : 'Optional'}
                            </Typography>
                          </Box>
                        </Box>

                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          placeholder="Type your answer here..."
                          value={answers[clarification.id] || ''}
                          onChange={(e) =>
                            handleAnswerChange(clarification.id, e.target.value)
                          }
                          disabled={readonly || loading || success}
                          variant="outlined"
                          InputProps={{
                            readOnly: readonly || clarification.is_answered,
                          }}
                          helperText={
                            clarification.is_answered && clarification.answered_at
                              ? `Answered on ${EstimationService.formatDate(
                                  clarification.answered_at
                                )}`
                              : clarification.is_required
                              ? 'This question requires an answer'
                              : 'Optional'
                          }
                        />
                      </Paper>
                    );
                  })}
              </Stack>
              {Object.keys(questionsByType).indexOf(type) <
                Object.keys(questionsByType).length - 1 && (
                <Divider sx={{ mt: 3 }} />
              )}
            </Box>
          ))}
        </Stack>

        {!readonly && !success && (
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading || progress < 100}
              startIcon={
                loading ? <CircularProgress size={20} /> : <SendIcon />
              }
            >
              {loading
                ? 'Submitting...'
                : progress < 100
                ? `Answer ${requiredQuestions.length - answeredRequired.length} More Required ${
                    requiredQuestions.length - answeredRequired.length === 1
                      ? 'Question'
                      : 'Questions'
                  }`
                : 'Submit Answers & Generate Specification'}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ClarificationQA;
